import { Badge, Divider } from 'antd';
import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import InputSearch from '../../components/Basic/InputSearch';
import Icon, { ICON_NAME } from '../../components/Icon';
import { openPopup } from '../../components/Popup/_components/WrapperPopup';
import PopupFilterVessel from '../../components/Popup/PopupFilterVessel';
import { IVessel } from '../../util/varibles/interface';
import { VesselSetting } from '../../util/varibles/userSetting';
import { updateUserSetting } from '../LoginPage/reducer';
import {
    getCurrentDuration,
    TVesselOverviewView,
    useVesselOverview,
} from './VesselOverviewContext';

const VIEW_OPTIONS: { value: TVesselOverviewView; label: string }[] = [
    { value: 'month', label: 'BY MONTH' },
    { value: 'year', label: 'BY YEAR' },
];

const ViewSwitcher: React.FC<{ disabled?: boolean }> = ({ disabled = false }) => {
    const [activeView, setStore] = useVesselOverview(state => state.activeView);
    const handleViewChange = useCallback((view: TVesselOverviewView) => {
        if (disabled || view === activeView) return;

        setStore({
            activeView: view,
            duration: getCurrentDuration(view),
        });
    }, [activeView, disabled, setStore]);

    return <div
        aria-disabled={disabled}
        className={`flex gap-2 ${disabled ? 'pointer-events-none opacity-50' : ''}`}
    >
        {VIEW_OPTIONS.map(item => (
            <div
                key={item.value}
                onClick={() => handleViewChange(item.value)}
                className={`
                    px-[16px] py-[9px] rounded-full
                    text-[12px] font-[600]
                    transition-colors duration-300 ease-in-out
                    ${activeView === item.value
                        ? 'bg-[var(--colorActive)] text-white cursor-default'
                        : 'text-inherit hover:bg-[var(--colorHover)] hover:text-[var(--colorActive)] cursor-pointer'
                    }
                `}
            >
                {item.label}
            </div>
        ))}
    </div>;
};

const VesselSearch: React.FC = () => {
    const [search, setStore] = useVesselOverview(state => state.search);

    return <InputSearch
        placeHolder='Search vessel by name'
        value={search}
        onChange={value => setStore({ search: value })}
    />;
};

const VesselFilter: React.FC<{ vessels: IVessel[] }> = ({ vessels }) => {
    const [vesselFilter, setStore] = useVesselOverview(state => state.vesselFilter);
    const dispatch = useDispatch();

    const handleOpenFilter = useCallback(() => {
        const div = openPopup(<PopupFilterVessel
            selected={vesselFilter}
            vessels={vessels}
            showCapabilities={false}
            onSave={value => {
                setStore({ vesselFilter: value })
                dispatch(updateUserSetting([{ [VesselSetting.VESSEL_FILTER]: value }]))
            }}
            onClose={() => div.remove()}
        />);
    }, [dispatch, setStore, vesselFilter, vessels]);

    return <div className='bt-ghost' onClick={handleOpenFilter}>
        <Badge className='mt-[2px]' dot={vesselFilter.length > 0} color="green">
            <Icon icon={ICON_NAME.FILTER} />
        </Badge>
        <span>Filter</span>
    </div>;
};

const VesselOverviewHeader: React.FC<{
    vessels: IVessel[];
    loading?: boolean;
}> = React.memo(({ vessels, loading = false }) => (
    <div className='flex items-center justify-between'>
        <ViewSwitcher disabled={loading} />
        <div className='flex items-center gap-[10px]'>
            <VesselSearch />
            <Divider orientation='vertical' className='h-[28px]' />
            <div className='flex items-center gap-2'>
                <VesselFilter vessels={vessels} />
            </div>
        </div>
    </div>
));

export default VesselOverviewHeader;
